import {ComponentType} from 'react'
import {SvgIconProps} from '@material-ui/core/SvgIcon'
import AccountBalanceWalletOutlined from '@material-ui/icons/AccountBalanceWalletOutlined'
import LayersOutlined from '@material-ui/icons/LayersOutlined'
import PeopleOutlined from '@material-ui/icons/PeopleOutlined'
import TrendingUpOutlined from '@material-ui/icons/TrendingUpOutlined'
import {Trans} from 'react-i18next'
import messages from '../../../assets/messages'
import {getFormattedAmount} from '../../../common/utils/general'
import {COLORS} from '../constants'
import {NetworkStats} from '../utils/networkStats'
import {Grid, KpiCard, KpiCardTop, KpiLabel, KpiValue, SkeletonCard} from './NetworkKpiCards.styled'

type KpiCardConfig = {
  label: {i18nKey: string; defaults: string}
  value: string
  icon: ComponentType<SvgIconProps>
  iconColor: string
  valueColor: string
}

/** Number of summary cards rendered, mirrored by the skeleton so the switch causes no reflow. */
const CARD_COUNT = 4

type NetworkKpiCardsProps = {
  stats: NetworkStats
  currency: string
  /**
   * True while the figures belong to a view the reader has just switched away from (PT-5717).
   * Placeholders are shown instead of the stale numbers, so no card ever displays a total that
   * describes a different network than the tree below it.
   */
  recalculating?: boolean
}

/**
 * Headline KPI cards summarising the whole offers network shown above the tree
 * (Network AUM, Total Investors, Sub-Agents).
 *
 * @param stats - Aggregated network figures produced by `aggregateNetworkStats`.
 * @param currency - ISO currency code used to format the Network AUM value.
 * @param recalculating - Renders the shimmering placeholder grid instead of the figures.
 */
export const NetworkKpiCards = ({stats, currency, recalculating = false}: NetworkKpiCardsProps) => {
  if (recalculating) {
    return (
      <Grid data-testid="pamm-network-kpi-skeleton" aria-hidden="true">
        {Array.from({length: CARD_COUNT}, (_, index) => (
          <SkeletonCard key={index} />
        ))}
      </Grid>
    )
  }

  const cards: KpiCardConfig[] = [
    {
      label: messages.pammNetworkAum,
      value: getFormattedAmount({amount: stats.networkAum, currency}),
      icon: AccountBalanceWalletOutlined,
      iconColor: COLORS.brand,
      valueColor: COLORS.brand,
    },
    {
      label: messages.totalInvestors,
      value: String(stats.totalInvestors),
      icon: PeopleOutlined,
      iconColor: COLORS.foreground,
      valueColor: COLORS.foreground,
    },
    {
      label: messages.pammNetworkSubAgents,
      value: String(stats.subAgentCount),
      icon: LayersOutlined,
      iconColor: COLORS.foreground,
      valueColor: COLORS.foreground,
    },
    // What the network produced in fees, summed over every node in the rendered tree — for a Money
    // Manager that is the whole downline's contribution reaching them (PT-5717).
    {
      label: messages.pammNetworkPfCollected,
      value: getFormattedAmount({amount: stats.pfCollected, currency}),
      icon: TrendingUpOutlined,
      iconColor: COLORS.success,
      valueColor: COLORS.success,
    },
  ]

  return (
    <Grid>
      {cards.map((card) => {
        const Icon = card.icon
        return (
          <KpiCard key={card.label.i18nKey}>
            <KpiCardTop>
              <KpiLabel>
                <Trans {...card.label} />
              </KpiLabel>
              <Icon style={{fontSize: 18, color: card.iconColor}} />
            </KpiCardTop>
            <KpiValue style={{color: card.valueColor}}>{card.value}</KpiValue>
          </KpiCard>
        )
      })}
    </Grid>
  )
}
